import { ApiError } from "@/helpers/api-error";

interface RequestOptions {
  /** When true, a 401 response will throw an ApiError instead of redirecting to login. */
  skipAuthRedirect?: boolean;
}

type RequestFn = <T = any>(
  url: RequestInfo | URL,
  body?: any,
  options?: RequestOptions
) => Promise<T>;

export const fetchWrapper = {
  get: request("GET") as RequestFn,
  post: request("POST") as RequestFn,
  put: request("PUT") as RequestFn,
  delete: request("DELETE") as RequestFn,
};

function request(method: string) {
  return async (
    url: RequestInfo | URL,
    body?: any,
    options?: RequestOptions
  ) => {
    const requestOptions: RequestInit = {
      method,
      headers: new Headers(),
      credentials: "include",
    };

    // Allows the server to detect AJAX requests and return 401 instead of 302
    // so we can redirect manually (fetch doesn't expose 302 redirects)
    (requestOptions.headers as Headers).set(
      "X-Requested-With",
      "XMLHttpRequest"
    );

    if (body) {
      (requestOptions.headers as Headers).set(
        "Content-Type",
        "application/json"
      );
      requestOptions.body = JSON.stringify(body);
    }

    try {
      return await fetch(url, requestOptions).then((res) =>
        handleResponse(res, options)
      );
    } catch (error) {
      if (error instanceof ApiError) throw error;
      throw new ApiError({
        status: 0,
        title: "Connection Error",
        detail:
          "Unable to connect to the server. Please check your connection or try again later.",
      });
    }
  };
}

function handleResponse(
  response: {
    url: any;
    text: () => Promise<any>;
    ok: any;
    status: number;
    statusText: any;
    headers: any;
  },
  options?: RequestOptions
) {
  return response.text().then((text) => {
    const data = text ? JSON.parse(text) : null;

    if (!response.ok) {
      if (response.status === 401 && !options?.skipAuthRedirect) {
        redirectToLogin();
        throw new ApiError({ status: 401, title: "Unauthorised" });
      }

      throw new ApiError({
        type: data?.type,
        title: data?.title,
        status: response.status,
        detail: data?.detail,
        errors: data?.errors,
        traceId: data?.traceId,
        // Run-readiness 422 (ADR-033) — forwarded so the pre-run confirm can render them.
        issues: data?.issues,
        advisories: data?.advisories,
        // Template-validation 400 — forwarded so the designer can badge the failing method.
        failures: data?.failures,
      });
    }

    return data;
  });
}

function redirectToLogin() {
  const currentUrl = new URL(window.location.href);
  const isPublicAuthRoute =
    currentUrl.pathname === "/login" ||
    currentUrl.pathname === "/forgot-password" ||
    currentUrl.pathname === "/invite/accept";

  if (isPublicAuthRoute) {
    return;
  }

  const returnUrl = encodeURIComponent(currentUrl.toString());
  window.location.replace(`/login?returnUrl=${returnUrl}`);
}
